import { Draggable, DraggableProvided } from '@hello-pangea/dnd';
import { Cancel as CancelIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import classNames from 'classnames';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import { ReactComponent as Burger } from 'new/components/icons/burger.svg';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { RemoveIconButton } from 'new/elements/Button/RemoveIconButton';
import { TextField } from 'new/elements/TextField';
import { reorder } from 'new/utils';
import React, { useMemo } from 'react';
import { DraggableVerticalList } from '../DraggableVerticalList';
import classes from './DataSetSplitDialog.module.scss';

export interface ValueItem {
  __id: string;
  valueList: (string | number | boolean)[];
  dataSetId?: string;
}

interface Props {
  isReadOnly?: boolean;
  highlightedId?: string | null;
  attr: string;
  parentDataSetSchema: any[];
  values: ValueItem[];
  setAttr: (attr: string) => void;
  setValues: (values: ValueItem[]) => void;
}

export default function ValuesEditor({
  isReadOnly = false,
  highlightedId,
  attr,
  values,
  parentDataSetSchema,
  setAttr,
  setValues,
}: Props) {
  // TODO: initialize from splitterDataSet if available
  const attrOptions = useMemo(
    () =>
      parentDataSetSchema?.map(({ __name }) => ({
        label: __name,
        value: __name,
      })),
    [parentDataSetSchema],
  );
  const selectedAttrOption = getCurrentSelectedOption(attrOptions, attr, null, null, true);

  const onAddOutputDataSet = () => {
    const newValues = [
      ...values,
      {
        __id: crypto.randomUUID(),
        valueList: [''],
      },
    ];
    setValues(newValues);
  };

  const onRemoveOutputDataSet = (event) => {
    const { index } = event.currentTarget.dataset;
    const newValues = [...values];
    newValues.splice(index, 1);
    setValues(newValues);
  };

  const onAddValue = (event) => {
    const { index: outputDataSetIndex } = event.currentTarget.dataset;
    const newValues = [...values];
    newValues[outputDataSetIndex] = {
      ...newValues[outputDataSetIndex],
      valueList: [...newValues[outputDataSetIndex].valueList, ''],
    };
    setValues(newValues);
  };

  const onRemoveValue = (event) => {
    const { index: valueIndex, outputDataSetIndex } = event.currentTarget.dataset;
    const newValues = [...values];
    const dataSetValues = newValues[outputDataSetIndex];
    const newValueList = [...dataSetValues.valueList];
    newValueList.splice(valueIndex, 1);
    newValues[outputDataSetIndex] = {
      ...dataSetValues,
      valueList: newValueList,
    };
    setValues(newValues);
  };

  const onChangeValue = (value: string, outputDataSetIndex: number, valueIndex: number) => {
    let convertedValue: string | number | boolean = value;

    // Check if the user wrapped the value in quotes
    const hasQuotes =
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"));

    if (hasQuotes) {
      // If user added quotes, treat as literal string (keep the quotes)
      convertedValue = value;
    } else {
      // For unquoted values, try to auto-convert to appropriate type
      if (value.toLowerCase() === 'true') {
        convertedValue = true;
      } else if (value.toLowerCase() === 'false') {
        convertedValue = false;
      } else {
        // Check if it's a number
        const numValue = Number(value);
        if (!isNaN(numValue) && value !== '') {
          convertedValue = numValue;
        }
      }
    }

    const newValues = [...values];
    const dataSetValues = newValues[outputDataSetIndex];
    const newValueList = [...dataSetValues.valueList];
    newValueList[valueIndex] = convertedValue;
    newValues[outputDataSetIndex] = {
      ...dataSetValues,
      valueList: newValueList,
    };
    setValues(newValues);
  };

  const onDragEnd = ({ source, destination }) => {
    if (typeof destination?.index !== 'undefined') {
      setValues(reorder(values, source?.index, destination?.index));
    }
  };

  const renderDraggableList = (list: ValueItem[]) => {
    return list.map((item, outputDataSetIndex) =>
      isReadOnly ? (
        renderListItem(list, item, outputDataSetIndex)
      ) : (
        <Draggable index={outputDataSetIndex} key={item.__id} draggableId={item.__id}>
          {(provided) => renderListItem(list, item, outputDataSetIndex, provided)}
        </Draggable>
      ),
    );
  };

  const renderListItem = (
    list: ValueItem[],
    { __id, valueList, dataSetId }: ValueItem,
    outputDataSetIndex: number,
    provided: DraggableProvided = null,
  ) => {
    return (
      <li
        key={__id}
        id={__id}
        ref={provided?.innerRef || null}
        {...(provided?.draggableProps || {})}
      >
        <div
          className={classNames(classes.listItem, {
            [classes.highlighted]: highlightedId === dataSetId,
          })}
        >
          <div className={classes.listItemTopRow}>
            {!isReadOnly && (
              <div className={classes.dragHandle} {...(provided?.draggableProps || {})}>
                <Burger />
              </div>
            )}
            <div className={classes.valueListContainer}>
              <div className={classes.valueList}>
                {isReadOnly ? (
                  <span
                    className={classNames(classes.radioGroupLabel, {
                      [classes.readOnlyValue]: isReadOnly,
                      [classes.readOnlyValueList]: isReadOnly,
                    })}
                  >
                    {valueList.join(', ') || '(No values)'}
                  </span>
                ) : (
                  valueList.map((value, valueIndex) => (
                    <div key={valueIndex} className={classes.valueItem}>
                      <TextField
                        fullWidth
                        value={String(value)}
                        onChange={(e) =>
                          onChangeValue(e.target.value, outputDataSetIndex, valueIndex)
                        }
                      />
                      {valueList.length > 1 && (
                        <IconButton
                          data-index={valueIndex}
                          data-output-data-set-index={outputDataSetIndex}
                          onClick={onRemoveValue}
                          title="Remove Value"
                        >
                          <CancelIcon />
                        </IconButton>
                      )}
                    </div>
                  ))
                )}
                {!isReadOnly && (
                  <div>
                    <button
                      data-index={outputDataSetIndex}
                      className={classes.addConditionButton}
                      onClick={onAddValue}
                    >
                      + Add another value
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className={classes.targetNexsetInline}>
              <span className={classes.targetLabel}>{dataSetId ? `to` : ''}</span>
              {dataSetId ? (
                <div className={classes.nexsetIdChip}>
                  <NexsetIcon fill="black" />
                  <span className={classes.nexsetId}>ID {dataSetId}</span>
                </div>
              ) : (
                <div className={classes.nexsetNumberChip}>
                  <span className={classes.nexsetNumber}>#{outputDataSetIndex + 1}</span>
                </div>
              )}
            </div>
            {!isReadOnly && (
              <RemoveIconButton
                tooltipText={
                  list.length === 1 ? 'Cannot remove the last condition' : 'Remove Condition'
                }
                disabled={list.length === 1}
                additionalProps={{ 'data-index': outputDataSetIndex }}
                onClickRemove={onRemoveOutputDataSet}
              />
            )}
          </div>
        </div>
      </li>
    );
  };

  const menuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;

  return (
    <div>
      <div className={classes.attributeSection}>
        <label className={classes.attributeLabel}>Attribute:</label>
        <div className={classes.attributeSelect}>
          {isReadOnly ? (
            <span
              className={classNames(classes.radioGroupLabel, {
                [classes.readOnlyValue]: isReadOnly,
              })}
            >
              {attr || '(No attribute)'}
            </span>
          ) : (
            <SortedSelect
              variant="default"
              value={selectedAttrOption}
              components={{ Input: SelectInput, Option: SelectOption }}
              options={attrOptions}
              isCreatable
              isClearable={false}
              backspaceRemovesValue={false}
              isSearchable
              onChange={(currValue) => {
                const newValue = getNewSelectionValue(currValue, true);
                setAttr(newValue);
              }}
              menuPortalTarget={menuPortalTarget}
            />
          )}
        </div>
      </div>
      <DraggableVerticalList
        items={values}
        listClassname="values-list"
        onDragEnd={onDragEnd}
        renderDraggableList={renderDraggableList}
      />
      {!isReadOnly && (
        <button
          className={classes.addConditionButton}
          disabled={values.length >= 5}
          onClick={onAddOutputDataSet}
        >
          + Add output Nexset
        </button>
      )}
    </div>
  );
}
